import {Link, useLocation, useNavigate} from "react-router-dom";
import {useEffect, useState} from "react";
import {Menu, MenuButton, MenuItem, MenuItems} from "@headlessui/react";
import {BsFilter, BsSearch} from "react-icons/bs";
import axios from "axios";
import {useQuery} from "react-query";
import {EmptyState} from "./EmptyState";
import {StatusCard} from "./StatusCard";
import {TableData} from "./TableData";
import {getStatusType} from "../utils/status";
import {calculateCreditsFromWeight} from "../utils/zone";

export const STATUS_TYPES = {
  PENDING: {title: "Pending", type: "pending"},
  COMPLETED: {title: "Completed", type: "completed"},
  CANCELLED: {title: "Cancelled", type: "cancelled"},
  PICKED_UP: {title: "Picked Up", type: "completed"},
  PROCESSING: {title: "Processing", type: "pending"},
  PICKED_UP_SCHEDULED: {title: "Pickup Scheduled", type: "pending"},
};

const STATUS_OPTIONS = [
  {label: "All", value: ""},
  {label: "Cancelled", value: "CANCELLED"},
  {label: "Executing", value: "EXECUTING"},
  {label: "Completed", value: "COMPLETED"},
  {label: "Package at Hub", value: "PACKAGE_AT_HUB"},
  {label: "In Hub", value: "IN_HUB"},
];

const TABLE_HEAD = [
  "",
  "Date",
  "Order ID",
  "Source",
  "Delivery Category",
  "Price",
  "Credit used",
  "Item count",
  "Status",
  "Action",
];

export const DraftOrders = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const Baseurl = process.env.REACT_APP_BASEURL;
  const token = JSON.parse(localStorage.getItem("eftd-admin-token") || "{}")
    ?.data?.data?.token;

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);

  // Get current page from URL or default to 1
  const queryParams = new URLSearchParams(location.search);
  const currentPage = Number(queryParams.get("page")) || 1;

  const config = {
    headers: {Authorization: `Bearer ${token}`},
  };

  // Debounce search input
  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchQuery]);

  const getOrders = async () => {
    let url = `${Baseurl}/api/admin/orders?limit=${limit}&page=${currentPage}&filter=orderStatus:DRAFT&sort=updatedAt:DESC`;

    if (debouncedSearchQuery) {
      url += `&query=${encodeURIComponent(debouncedSearchQuery)}`;
    }

    const response = await axios.get(url, {...config});
    setTotalPages(Math.ceil(response?.data?.data?.meta?.total / limit));
    return response?.data?.data?.items;
  };

  const {
    data: orderData,
    isLoading: isLoadingOrderData,
    refetch,
    isRefetching: isRefetchingOrderData,
  } = useQuery(
    ["DRAFT_ORDERS", currentPage, limit, debouncedSearchQuery, selectedStatus],
    getOrders,
    {
      keepPreviousData: true,
    }
  );

  useEffect(() => {
    refetch();
  }, [debouncedSearchQuery, selectedStatus, currentPage, limit, refetch]);

  const handlePageChange = (newPage: number) => {
    navigate(`?page=${newPage}`);
  };

  return (
    <div className="w-full rounded-lg border border-gray-200">
      <div className="flex items-center justify-between px-6 py-4">
        <h3 className="text-xl font-bold text-gray-700">Draft Orders</h3>

        <div className="flex gap-3">
          <div className="relative h-12 bg-gray-50 border rounded-lg w-96 border-gray-300">
            <input
              type="text"
              placeholder="Search orders by ID"
              className="w-full h-full pl-12 text-sm bg-transparent outline-none placeholder:text-gray-400"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <BsSearch className="absolute left-4 top-3.5 text-gray-400" />
          </div>
        </div>
      </div>

      {/* Loading state */}
      {isLoadingOrderData || isRefetchingOrderData ? (
        <div className="py-10 text-center text-gray-400 text-sm">
          Loading...
        </div>
      ) : orderData?.length > 0 ? (
        <>
          <div className="overflow-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {TABLE_HEAD.map((head) => (
                    <th
                      key={head}
                      className="p-3 text-xs font-medium text-gray-500 text-left"
                    >
                      {head}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {orderData.map((order: any) => {
                  const status = getStatusType(order.orderStatus);
                  const totalCreditsNeeded = order.deliveryDetails.reduce(
                    (total: number, delivery: any) => {
                      const weight = parseFloat(
                        delivery.itemWeight.replace("kg", "")
                      );
                      return total + calculateCreditsFromWeight(weight);
                    },
                    0
                  );
                  return (
                    <tr
                      key={order.id}
                      className="border-b border-gray-100 hover:bg-gray-100 transition-colors"
                    >
                      <td className="py-5 px-1">
                        {order.hasPendingTopupRequest && (
                          <svg
                            width="21"
                            height="21"
                            viewBox="0 0 21 21"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              fill-rule="evenodd"
                              clip-rule="evenodd"
                              d="M8.51331 3.53508C9.39447 2.00263 11.6055 2.00263 12.4866 3.53508L19.1891 15.1915C20.0676 16.7193 18.9648 18.6255 17.2024 18.6255H3.79751C2.03519 18.6255 0.932383 16.7193 1.81085 15.1915L8.51331 3.53508ZM10.4998 7.37516C10.845 7.37516 11.1248 7.65498 11.1248 8.00016V11.3335C11.1248 11.6787 10.845 11.9585 10.4998 11.9585C10.1547 11.9585 9.87484 11.6787 9.87484 11.3335V8.00016C9.87484 7.65498 10.1547 7.37516 10.4998 7.37516ZM10.9727 15.0841C11.2036 14.8276 11.1828 14.4324 10.9263 14.2015C10.6697 13.9706 10.2745 13.9913 10.0436 14.2479L10.0353 14.2572C9.80437 14.5137 9.82517 14.9089 10.0817 15.1398C10.3383 15.3708 10.7335 15.35 10.9644 15.0934L10.9727 15.0841Z"
                              fill="#DF1C41"
                            />
                          </svg>
                        )}
                      </td>

                      <TableData
                        content={new Date(order.updatedAt).toLocaleDateString()}
                      />
                      <TableData content={order.orderId} />
                      <TableData content={order.source || "NILL"} />
                      <TableData content={order.deliveryType} />
                      <TableData
                        content={`₦${parseInt(
                          order.estimatedCost
                        ).toLocaleString()}`}
                      />
                      <TableData
                        content={
                          order.paymentMode === "SUBSCRIPTION"
                            ? totalCreditsNeeded
                            : "N/A"
                        }
                      />
                      <TableData content={order.deliveryDetails.length} />

                      <td className="p-3">
                        <StatusCard title={status.title} type={status.type} />
                      </td>
                      <td className="p-3">
                        <Link
                          to={
                            order.deliveryDetails.length > 1
                              ? `/orders/multiple/${order.id}`
                              : `/orders/${order.id}`
                          }
                          className="text-sm font-semibold text-orange-500 hover:text-orange-600"
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
            <div className="flex items-center gap-6">
              <p className="text-sm text-gray-500">
                Showing page {currentPage} of {totalPages}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`px-4 py-2 text-sm font-medium rounded-md border ${
                    currentPage === 1
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Previous
                </button>
                {/* {Array.from({length: totalPages}, (_, i) => i + 1).map((page) => (
				<button
				  key={page}
				  onClick={() => handlePageChange(page)}
				  className={`px-4 py-2 text-sm font-medium rounded-md border ${
					currentPage === page
					  ? "bg-orange-500 text-white"
					  : "bg-white text-gray-700 hover:bg-gray-50"
				  }`}
				>
				  {page}
				</button>
			  ))} */}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`px-4 py-2 text-sm font-medium rounded-md border ${
                    currentPage === totalPages
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Next
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <p className="text-sm text-gray-500">Items per page:</p>

              <select
                name=""
                id=""
                onChange={(
                  e: React.ChangeEvent<
                    HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
                  >
                ) => {
                  setLimit(Number(e.target.value));
                }}
                className="border border-gray-500 rounded py-1 px-2 text-sm text-gray-500"
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="30">30</option>
                <option value="40">40</option>
                <option value="50">50</option>
              </select>
            </div>
          </div>
        </>
      ) : (
        <div className="py-14">
          <EmptyState type="order" text="No Orders Found" />
        </div>
      )}
    </div>
  );
};
